"use client";

import { JSX, useState } from "react";
import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { useHoverCapable } from "@/lib/hooks/use-hover-capable";
import { SegmentTabs } from "@/components/motion/segment-tabs";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
  LegendValue,
  useLegendItem,
} from "@/components/charts/legend";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import { useLegendHoverSelect } from "@/lib/hooks/use-hover-select";
import type { ModuleBeforeAfterAnnotatedData } from "@/types/modules";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { importsTokenScope } from "./imports-token-scope";

type Mode = "before" | "after";

function AnnotationRow() {
  const { isHovered, isSelected } = useLegendItem();
  const emphasized = isHovered || isSelected;
  return (
    <LegendItem className="flex shrink-0 items-start gap-2 rounded-md px-2 py-2 transition-colors">
      <LegendMarker
        variant="number"
        className={cn(
          "mt-0.5 transition-colors",
          emphasized ? "text-[var(--fg)]" : "text-[var(--muted)]",
        )}
      />
      <div className="flex flex-col items-start gap-0.5">
        <LegendLabel
          className={cn(
            "whitespace-nowrap text-sm transition-colors",
            emphasized ? "text-[var(--fg)]" : "text-[var(--muted)]",
          )}
        />
        <LegendValue className="whitespace-nowrap text-xs tabular-nums text-[var(--subtle)]" />
      </div>
    </LegendItem>
  );
}

export default function ModuleBeforeAfterAnnotated({
  data,
  grayscale,
  accent,
  className,
}: {
  data: ModuleBeforeAfterAnnotatedData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t } = useViewerLanguage();
  const annotations = data.annotations;
  const beforeLabel = data.beforeLabel ?? t("Before");
  const afterLabel = data.afterLabel ?? t("After");
  const aspectRatio = data.aspectRatio ?? "16 / 10";
  const defaultMode = data.defaultMode ?? "after";
  const beforeSrc = data.beforeSrc;
  const afterSrc = data.afterSrc;
  const beforeAlt = data.beforeAlt ?? beforeLabel;
  const afterAlt = data.afterAlt ?? afterLabel;

  const [mode, setMode] = useState<Mode>(defaultMode);
  const annotation = useLegendHoverSelect();
  const effective = annotation.effective;
  const hasHover = useHoverCapable();
  const isAfter = mode === "after";

  const legendItems = annotations.map((a) => ({
    id: a.id,
    label: stripInlineMarkdown(a.title),
    value: stripInlineMarkdown(a.detail),
    color: "var(--fg)",
  }));

  return (
    <div
      className={cn("w-full", className)}
      style={importsTokenScope(grayscale, accent)}
    >
      <div className="mb-4 flex items-center justify-between gap-3">
        <SegmentTabs
          value={mode}
          onValueChange={(v) => v !== null && setMode(v as Mode)}
          options={[
            { value: "before", label: beforeLabel },
            { value: "after", label: afterLabel },
          ]}
        />
        <span className="tabular-nums text-sm text-[var(--subtle)]">
          {annotations.length} changes
        </span>
      </div>

      <div
        className="relative w-full overflow-hidden rounded-xl bg-black/[0.03] ring-1 ring-black/[0.1] dark:bg-white/[0.05] dark:ring-white/[0.14]"
        style={{ aspectRatio }}
      >
        <img
          src={beforeSrc}
          alt={beforeAlt}
          className={cn("absolute inset-0 h-full w-full object-cover", grayscale && "grayscale")}
          style={{
            opacity: isAfter ? 0 : 1,
            transition: "opacity 0.35s ease",
          }}
        />
        <img
          src={afterSrc}
          alt={afterAlt}
          className={cn("absolute inset-0 h-full w-full object-cover", grayscale && "grayscale")}
          style={{
            opacity: isAfter ? 1 : 0,
            transition: "opacity 0.35s ease",
          }}
        />

        {annotations.map((a, i) => {
          const isHovered = effective === i;
          const dim = effective !== null && !isHovered;
          return (
            <button
              key={a.id}
              type="button"
              aria-label={a.title}
              aria-pressed={annotation.selected === i}
              onMouseEnter={hasHover ? () => annotation.setHovered(i) : undefined}
              onMouseLeave={hasHover ? () => annotation.setHovered(null) : undefined}
              onFocus={hasHover ? () => annotation.setHovered(i) : undefined}
              onBlur={hasHover ? () => annotation.setHovered(null) : undefined}
              onClick={() => annotation.select(i, { kind: "point", id: a.id })}
              className="absolute flex h-6 w-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-xs font-medium tabular-nums transition-all"
              style={{
                left: `${a.x}%`,
                top: `${a.y}%`,
                background: "rgba(255,255,255,0.95)",
                color: "#0a0a0a",
                boxShadow: isHovered
                  ? "0 0 0 5px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.2)"
                  : "0 1px 2px rgba(0,0,0,0.2)",
                opacity: dim ? 0.35 : 1,
              }}
            >
              {i + 1}
            </button>
          );
        })}
      </div>

      <SwipeScroll className="mt-4 w-full border-t border-dashed border-[var(--border-dashed-soft)] pt-3">
        <Legend
          items={legendItems}
          hoveredIndex={annotation.hovered}
          onHoverChange={annotation.setHovered}
          selectedIndex={annotation.selected}
          onSelect={annotation.select}
          onLabelRef={annotation.setLabelRef}
          className="flex w-max flex-row gap-1"
        >
          <AnnotationRow />
        </Legend>
      </SwipeScroll>
    </div>
  );
}
